import { getLog } from '@eigtech/ui-shared-logging'
import { useAudioVideo, useVideoInputs } from 'amazon-chime-sdk-component-library-react'
import { useCallback, useEffect, useRef, useState } from 'react'

const log = getLog()

interface TorchMediaTrackSettings extends MediaTrackSettings {
  torch?: boolean
}

type TorchableTrack = MediaStreamTrack & {
  getCapabilities: () => MediaTrackCapabilities & { torch?: boolean }
  getSettings: () => TorchMediaTrackSettings
  applyConstraints: (constraints: MediaTrackConstraints) => Promise<void>
}

export function useTorch() {
  const { selectedDevice } = useVideoInputs()
  const [isTorchOn, setIsTorchOn] = useState<boolean>(false)
  const [isTorchSupported, setIsTorchSupported] = useState<boolean>(false)
  const trackRef = useRef<TorchableTrack | null>(null)

  const audioVideo = useAudioVideo()

  useEffect(() => {
    async function detectTrack() {
      if (!audioVideo) {
        setIsTorchSupported(false)
        trackRef.current = null
        return
      }

      if (!selectedDevice) return

      try {
        const stream = await audioVideo.startVideoInput(selectedDevice)
        const videoTracks = stream?.getVideoTracks() ?? []
        if (videoTracks.length === 0) {
          setIsTorchSupported(false)
          trackRef.current = null
          return
        }

        const track = videoTracks[0] as TorchableTrack
        const capabilities = track.getCapabilities()

        if (!('torch' in capabilities) || !capabilities.torch) {
          setIsTorchSupported(false)
          trackRef.current = null
          return
        }

        trackRef.current = track
        setIsTorchSupported(true)
        const settings = track.getSettings() as TorchMediaTrackSettings
        setIsTorchOn(!!settings.torch)
      } catch (error) {
        log.error('could not detect torch track', error)
        setIsTorchSupported(false)
        trackRef.current = null
      }
    }

    detectTrack()

    return () => {
      trackRef.current = null
    }
  }, [audioVideo, selectedDevice])

  const toggleTorch = useCallback(async () => {
    const track = trackRef.current
    if (!track) {
      log.warn('Torch toggle called but no track available')
      return
    }
    try {
      // @ts-expect-error MediaTrackConstraintSet says torch doesn't exist as a property
      await track.applyConstraints({ advanced: [{ torch: !isTorchOn }] })
      const settings = track.getSettings() as TorchMediaTrackSettings
      const isNowOn = !!settings.torch
      setIsTorchOn(isNowOn)
    } catch (error) {
      log.error('Error toggling torch', error)
    }
  }, [isTorchOn])

  return {
    isTorchOn,
    isTorchSupported,
    toggleTorch,
  }
}
